import { useEffect, useState } from "react";
import { BadgeCheck, FileUp, Download, Trash2, Loader2, Receipt, Ban } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACTIVATION_TYPES = ["Manual", "Complimentary", "Promotional", "Internal", "Custom"];

export default function CustomerManageDialog({ user, onClose, onUpdated }) {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(user.status || "active");
  const [busyStatus, setBusyStatus] = useState(false);
  const isAdmin = user.role === "super_admin" || user.role === "admin";
  const toggleStatus = async () => {
    const next = status === "suspended" ? "active" : "suspended";
    if (next === "suspended" && !window.confirm(`Deactivate ${user.name}? They'll be signed out immediately and blocked from logging in until reactivated.`)) return;
    setBusyStatus(true);
    try {
      await api.post(`/admin/customers/${user.id}/status`, { status: next });
      setStatus(next);
      toast.success(next === "suspended" ? "Account deactivated — user signed out" : "Account reactivated");
      onUpdated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyStatus(false); }
  };
  const [subs, setSubs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [act, setAct] = useState({ plan_id: "", activation_type: "Manual", duration_days: "", reason: "", notes: "" });
  const [inv, setInv] = useState({ invoice_number: "", invoice_date: "", amount: "", currency: "INR", billing_period: "", payment_reference: "", notes: "", send_to_customer: false });
  const [file, setFile] = useState(null);
  const [busyAct, setBusyAct] = useState(false);
  const [busyInv, setBusyInv] = useState(false);

  const loadSubs = () => api.get(`/admin/subscriptions?user_id=${user.id}`).then((r) => setSubs(r.data.subscriptions)).catch(() => setSubs([]));
  const loadInvoices = () => api.get(`/admin/invoices?user_id=${user.id}`).then((r) => setInvoices(r.data.invoices)).catch(() => setInvoices([]));
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    loadSubs(); loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const activate = async () => {
    if (!act.plan_id) { toast.error("Choose a plan"); return; }
    setBusyAct(true);
    try {
      await api.post("/admin/subscriptions/activate", {
        user_id: user.id, plan_id: act.plan_id, activation_type: act.activation_type,
        duration_days: act.duration_days ? Number(act.duration_days) : null,
        reason: act.reason, notes: act.notes,
      });
      toast.success(`${act.activation_type} plan activated — no payment created`);
      loadSubs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyAct(false); }
  };

  const uploadInvoice = async () => {
    if (!file) { toast.error("Choose an invoice file (PDF/JPG/PNG)"); return; }
    if (!inv.invoice_number.trim()) { toast.error("Enter an invoice number"); return; }
    setBusyInv(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("user_id", user.id);
      Object.entries(inv).forEach(([k, v]) => fd.append(k, k === "send_to_customer" ? (v ? "true" : "false") : (v ?? "")));
      const { data } = await api.post("/admin/invoices", fd);
      toast.success(data.emailed ? "Invoice saved & emailed to the customer" : "Invoice saved");
      setFile(null); setInv({ invoice_number: "", invoice_date: "", amount: "", currency: "INR", billing_period: "", payment_reference: "", notes: "", send_to_customer: false });
      loadInvoices();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyInv(false); }
  };

  const delInvoice = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`/admin/invoices/${id}`); toast.success("Invoice deleted"); loadInvoices(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="manage-user-dialog">
        <DialogHeader><DialogTitle>Manage · {user.name} <span className="text-sm font-normal text-slate-400">({user.email})</span></DialogTitle></DialogHeader>

        {/* Account status — activate / deactivate */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 p-4" data-testid="account-status-block">
          <div>
            <p className="text-sm font-semibold text-slate-800">Account status</p>
            <p className="text-xs text-slate-500">{status === "suspended" ? "Deactivated — signed out and blocked from logging in." : "Active — can log in and use the marketplace."}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${status === "suspended" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{status}</span>
            {isAdmin
              ? <span className="text-xs text-slate-400">Admins can't be deactivated</span>
              : <button onClick={toggleStatus} disabled={busyStatus} data-testid="toggle-user-status" className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${status === "suspended" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
                  {busyStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : (status === "suspended" ? <BadgeCheck className="h-4 w-4" /> : <Ban className="h-4 w-4" />)}
                  {status === "suspended" ? "Activate" : "Deactivate"}
                </button>}
          </div>
        </div>

        {/* Manual Activation */}
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><BadgeCheck className="h-4 w-4 text-emerald-500" /> Manual Activation <span className="text-xs font-normal text-slate-400">— activates a plan without payment (no fake payment record is created)</span></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs text-slate-500">Plan</Label>
              <Select value={act.plan_id || undefined} onValueChange={(v) => setAct({ ...act, plan_id: v })}>
                <SelectTrigger className="mt-1" data-testid="activate-plan"><SelectValue placeholder="Choose a plan" /></SelectTrigger>
                <SelectContent>{plans.map((p) => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs text-slate-500">Activation type</Label>
              <Select value={act.activation_type} onValueChange={(v) => setAct({ ...act, activation_type: v })}>
                <SelectTrigger className="mt-1" data-testid="activate-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIVATION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs text-slate-500">Duration (days, blank = no expiry)</Label>
              <Input type="number" min="0" value={act.duration_days} onChange={(e) => setAct({ ...act, duration_days: e.target.value })} className="mt-1" data-testid="activate-days" /></div>
            <div><Label className="text-xs text-slate-500">Reason</Label>
              <Input value={act.reason} onChange={(e) => setAct({ ...act, reason: e.target.value })} placeholder="e.g. Partner comp" className="mt-1" data-testid="activate-reason" /></div>
            <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Notes (internal)</Label>
              <Input value={act.notes} onChange={(e) => setAct({ ...act, notes: e.target.value })} className="mt-1" data-testid="activate-notes" /></div>
          </div>
          <button onClick={activate} disabled={busyAct} data-testid="activate-submit" className="mt-3 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {busyAct ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />} Activate plan
          </button>

          {subs.length > 0 && (
            <div className="mt-4 space-y-1.5" data-testid="user-subs">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Subscription history</p>
              {subs.map((s) => (
                <div key={s.plan_id + s.created_at} className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="font-medium text-slate-700">{s.plan_name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${["ACTIVE", "TRIAL", "COMPLIMENTARY"].includes(s.status) ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{s.status}</span>
                  {s.activation_type && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] text-indigo-600">{s.activation_type}</span>}
                  {s.ends && <span className="text-xs text-slate-400">ends {fmtDate(s.ends)}</span>}
                  {s.reason && <span className="text-xs text-slate-400">· {s.reason}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invoice upload */}
        <div className="mt-4 rounded-xl border border-slate-200 p-4">
          <div className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><Receipt className="h-4 w-4 text-blue-500" /> Subscription Invoice</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs text-slate-500">Invoice number *</Label>
              <Input value={inv.invoice_number} onChange={(e) => setInv({ ...inv, invoice_number: e.target.value })} className="mt-1" data-testid="invoice-number" /></div>
            <div><Label className="text-xs text-slate-500">Invoice date</Label>
              <Input type="date" value={inv.invoice_date} onChange={(e) => setInv({ ...inv, invoice_date: e.target.value })} className="mt-1" data-testid="invoice-date" /></div>
            <div><Label className="text-xs text-slate-500">Amount</Label>
              <Input type="number" min="0" value={inv.amount} onChange={(e) => setInv({ ...inv, amount: e.target.value })} className="mt-1" data-testid="invoice-amount" /></div>
            <div><Label className="text-xs text-slate-500">Currency</Label>
              <Input value={inv.currency} onChange={(e) => setInv({ ...inv, currency: e.target.value })} className="mt-1" data-testid="invoice-currency" /></div>
            <div><Label className="text-xs text-slate-500">Billing period</Label>
              <Input value={inv.billing_period} onChange={(e) => setInv({ ...inv, billing_period: e.target.value })} placeholder="e.g. Jun 2026 – Jul 2026" className="mt-1" data-testid="invoice-period" /></div>
            <div><Label className="text-xs text-slate-500">Payment reference (optional)</Label>
              <Input value={inv.payment_reference} onChange={(e) => setInv({ ...inv, payment_reference: e.target.value })} className="mt-1" data-testid="invoice-ref" /></div>
          </div>
          <div className="mt-3">
            <input id="invfile" type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="invoice-file" />
            <label htmlFor="invfile" className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-blue-600 hover:border-blue-400"><FileUp className="h-4 w-4" /> {file ? file.name : "Choose invoice (PDF/JPG/PNG)"}</label>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={inv.send_to_customer} onChange={(e) => setInv({ ...inv, send_to_customer: e.target.checked })} data-testid="invoice-send" /> Email the invoice to the customer
          </label>
          <button onClick={uploadInvoice} disabled={busyInv} data-testid="invoice-upload" className="mt-3 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {busyInv ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Save invoice
          </button>

          {invoices.length > 0 && (
            <div className="mt-4 space-y-1.5" data-testid="user-invoices">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Invoices</p>
              {invoices.map((iv) => (
                <div key={iv.invoice_id} className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="font-medium text-slate-700">{iv.invoice_number}</span>
                  {iv.invoice_date && <span className="text-xs text-slate-400">{fmtDate(iv.invoice_date)}</span>}
                  <span className="text-xs text-slate-500">{iv.currency} {iv.amount}</span>
                  <a href={`${API}/invoices/${iv.invoice_id}/file`} target="_blank" rel="noreferrer noopener noreferrer" className="ml-auto inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:border-blue-300" data-testid={`invoice-dl-${iv.invoice_id}`}><Download className="h-3 w-3" /> Open</a>
                  <button onClick={() => delInvoice(iv.invoice_id)} className="rounded-lg p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-500" data-testid={`invoice-del-${iv.invoice_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
